import { z } from 'zod';

/**
 * Contracten van de module signage (Stap 41): digitale schermen in de kantine, de kleedkamer of
 * de hal.
 *
 * Het oude platform koppelde een scherm via een leverancier (Pixee) met een S3-webhook en een
 * zwakke header. Dat wordt niet herbouwd: een scherm is hier **een browser die een URL opent**, en
 * daarmee werkt elke scherm-pc, elke smart-tv-browser en elke mediaspeler die een pagina kan
 * tonen. Waarom dat zo is, staat in ADR-0063.
 *
 * Twee regels bepalen de rest:
 *
 * 1. **Geen persoonsgegevens op een scherm.** Een scherm hangt in een gang; wie er langsloopt,
 *    heeft niets getekend. Daarom mag alleen een bericht aan **iedereen** zonder
 *    bevestigingsplicht erop, en staat er nooit een naam of een aantal lezers bij.
 * 2. **Een scherm heeft zijn eigen sleutel en geen sessie.** Het meldt zich één keer aan met een
 *    koppelcode die een beheerder voorleest, en houdt daarna een token. Een scherm dat met het
 *    account van een medewerker zou aanmelden, is een aanmelding die jaren in een kantine hangt.
 */

export const signageItemTypeSchema = z.enum(['post', 'media']);
export type SignageItemType = z.infer<typeof signageItemTypeSchema>;

// --- Schermen ------------------------------------------------------------------------

export const screenSchema = z.object({
  id: z.guid(),
  name: z.string(),
  /** Waar het hangt, in mensentaal: "Kantine", "Kleedkamer West". */
  location: z.string(),
  /**
   * De vestiging waar dit scherm hangt (Stap 29).
   *
   * Leeg betekent: het bedrijf heeft er maar één en benoemt hem niet. Met twee fabrieken is dit
   * het verschil tussen "de kantine" en "welke kantine".
   */
  siteId: z.guid().nullable().default(null),
  playlistId: z.guid().nullable(),
  playlistName: z.string().nullable(),
  /**
   * De koppelcode, zolang het scherm nog niet gekoppeld is.
   *
   * Zes tekens die een beheerder aan de telefoon kan voorlezen aan wie bij het scherm staat.
   * Verdwijnt zodra het scherm zich gemeld heeft: daarna is hij nutteloos én gevaarlijk.
   */
  pairingCode: z.string().nullable(),
  pairedAt: z.string().datetime().nullable(),
  /** Wanneer dit scherm het laatst zijn playlist ophaalde. Leeg betekent: nooit. */
  lastSeenAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});
export type Screen = z.infer<typeof screenSchema>;

export const screenListSchema = z.object({ items: z.array(screenSchema) });
export type ScreenList = z.infer<typeof screenListSchema>;

export const createScreenRequestSchema = z.object({
  name: z.string().trim().min(1, 'Geef het scherm een naam.').max(80),
  location: z.string().trim().max(120).default(''),
  playlistId: z.guid().nullable().default(null),
  /** Leeg laten bij een bedrijf met één vestiging. */
  siteId: z.guid().nullable().default(null),
});
export type CreateScreenRequest = z.infer<typeof createScreenRequestSchema>;

export const updateScreenRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  location: z.string().trim().max(120).optional(),
  playlistId: z.guid().nullable().optional(),
});
export type UpdateScreenRequest = z.infer<typeof updateScreenRequestSchema>;

/** Wat het scherm zelf stuurt om zich te koppelen, en wat het terugkrijgt. */
export const registerScreenRequestSchema = z.object({
  tenantCode: z.string().trim().min(1).max(60),
  pairingCode: z.string().trim().min(4).max(20),
});
export type RegisterScreenRequest = z.infer<typeof registerScreenRequestSchema>;

export const registeredScreenSchema = z.object({
  screenId: z.guid(),
  name: z.string(),
  /**
   * Het token van dit scherm. **Eén keer te zien**: daarna staat alleen een hash op de server.
   *
   * Het scherm bewaart het in zijn browser. Een token en geen sessie met verversing: een scherm
   * zonder toetsenbord kan niet opnieuw aanmelden, en een sessie die na twee weken verloopt, is
   * een leeg scherm op een maandagmorgen.
   */
  token: z.string(),
});
export type RegisteredScreen = z.infer<typeof registeredScreenSchema>;

// --- Playlists -----------------------------------------------------------------------

export const playlistItemSchema = z.object({
  id: z.guid(),
  type: signageItemTypeSchema,
  /** Het bericht-id of het bestand-id. */
  refId: z.guid(),
  /** Hoe lang dit item blijft staan. */
  durationSeconds: z.number().int().min(3).max(600),
  position: z.number().int(),
  validFrom: z.string().datetime().nullable(),
  validUntil: z.string().datetime().nullable(),
  /** Wat er van het item te zeggen is in het beheerscherm. */
  label: z.string(),
  /** Staat dit item nu op het scherm? Zo niet, waarom niet. */
  visible: z.boolean(),
  hiddenReason: z.string().nullable(),
});
export type PlaylistItem = z.infer<typeof playlistItemSchema>;

export const playlistSchema = z.object({
  id: z.guid(),
  name: z.string(),
  items: z.array(playlistItemSchema),
  screenCount: z.number().int(),
  createdAt: z.string().datetime(),
});
export type Playlist = z.infer<typeof playlistSchema>;

export const playlistListSchema = z.object({ items: z.array(playlistSchema) });
export type PlaylistList = z.infer<typeof playlistListSchema>;

export const createPlaylistRequestSchema = z.object({
  name: z.string().trim().min(1, 'Geef de playlist een naam.').max(80),
});
export type CreatePlaylistRequest = z.infer<typeof createPlaylistRequestSchema>;

export const addPlaylistItemRequestSchema = z.object({
  type: signageItemTypeSchema,
  refId: z.guid(),
  durationSeconds: z.number().int().min(3).max(600).default(15),
  validFrom: z.string().datetime().nullable().default(null),
  validUntil: z.string().datetime().nullable().default(null),
});
export type AddPlaylistItemRequest = z.infer<typeof addPlaylistItemRequestSchema>;

/** De berichten die een beheerder op een scherm mag zetten; de regel staat in ADR-0063. */
export const signageCandidateSchema = z.object({
  postId: z.guid(),
  title: z.string(),
  publishedAt: z.string().datetime(),
  /** Al op schermen te zien? Dan hoeft hij niet nog eens in een playlist. */
  onScreens: z.boolean(),
});
export type SignageCandidate = z.infer<typeof signageCandidateSchema>;

export const signageCandidateListSchema = z.object({
  items: z.array(signageCandidateSchema),
});
export type SignageCandidateList = z.infer<typeof signageCandidateListSchema>;

// --- Wat het scherm zelf ophaalt -----------------------------------------------------

/**
 * Eén dia.
 *
 * `bodyHtml` is de tekst van het bericht, zonder reacties, zonder lezers en zonder namen: een
 * scherm hangt in een gang en wie er langsloopt, heeft niets getekend. `imageUrl` is kortlevend en
 * ondertekend — daarom haalt het scherm de playlist elke minuut opnieuw op.
 */
export const slideSchema = z.object({
  id: z.guid(),
  type: signageItemTypeSchema,
  title: z.string(),
  bodyHtml: z.string(),
  imageUrl: z.string().nullable(),
  durationSeconds: z.number().int(),
});
export type Slide = z.infer<typeof slideSchema>;

export const screenPlaylistSchema = z.object({
  screenId: z.guid(),
  screenName: z.string(),
  tenantName: z.string(),
  /** De huisstijlkleur van het bedrijf, zodat een scherm in de gang van hén is. */
  brandColor: z.string().nullable(),
  logoUrl: z.string().nullable(),
  slides: z.array(slideSchema),
  /** Wanneer de server dit samenstelde; het scherm toont dat klein in de hoek. */
  generatedAt: z.string().datetime(),
  /** Hoe lang het scherm hoort te wachten voordat het opnieuw vraagt. */
  refreshSeconds: z.number().int(),
});
export type ScreenPlaylist = z.infer<typeof screenPlaylistSchema>;

/** Hoe vaak een scherm opnieuw vraagt. Eén minuut: acceptatiecriterium van Stap 41. */
export const SIGNAGE_REFRESH_SECONDS = 60;

/** Hoe lang een dia standaard blijft staan. */
export const SIGNAGE_DEFAULT_DURATION = 15;
